import { useReactiveVar } from '@apollo/client'
import React from 'react'

import { LoyaltyCardStamp } from '@src/components/SingleOutlet/types'
import {
  DetailedPaymentMethod,
  Discount,
  LoyaltyCard as LoyaltyCardType,
} from '@src/graphql-types'
import { MainRouteName } from '@src/hooks/useAccountRouter'
import { useFormatCurrency } from '@src/hooks/useFormatCurrency'
import { UseBasketTotalsResponseData } from '@src/hooks/useTotals/useBasketTotals'
import { paymentMethodVar } from '@src/hooks/useTotals/utils/paymentMethodReactiveVar'
import { imageJitURL } from '@src/utils/imageJitURL'

import {
  LoyaltyCardLinkContainer,
  LoyaltyCardImage,
  LoyaltyCardIconAndTextContainer,
  DescriptionAndStampsContainer,
  LoyaltyCardInnerContainer,
  LoyaltyCardTitle,
  OuterContainer,
  TitleAndDescriptionContainer,
} from './CustomerLoyaltyCard.styles'
import { LoyaltyCardInfoText } from './LoyaltyCardInfoText'
import { LoyaltyCardStampProgressMeter } from './StampProgressMeter'
import { ScallopedMaskSVGLarge } from '../SVGS/ScallopedMaskSVGLarge'

export type CustomerLoyaltyCard = Pick<
  LoyaltyCardType,
  | 'id'
  | 'requiredStamps'
  | 'discountStrategy'
  | 'allowCashOrders'
  | 'isAvailableToday'
  | 'loyaltyCardColor'
  | 'loyaltyCardIcon'
  | 'netTotal'
  | 'amountToSpendToEarnAStamp'
> & {
  accountsSectionTextOverride?: LoyaltyCardType['accountsSectionTextOverride']
  discount: Pick<
    Discount,
    | 'id'
    | 'minimumSubtotalGross'
    | 'name'
    | 'discountAmount'
    | 'discountPercentage'
  >
} & { loyaltyCardStamps: LoyaltyCardStamp[] }

export const CustomerLoyaltyCard: React.FC<{
  loyaltyCard: CustomerLoyaltyCard | null
  isTermsPage?: boolean
  basketTotals?: UseBasketTotalsResponseData
  isBasket?: boolean
  isFinalCard?: boolean
  singleBasketCard?: boolean
  useAccountsSectionTextOverride?: boolean
}> = ({
  loyaltyCard,
  isTermsPage = false,
  basketTotals,
  singleBasketCard = false,
  isBasket = false,
  isFinalCard = false,
  useAccountsSectionTextOverride = false,
}) => {
  const formatCurrency = useFormatCurrency()
  const paymentMethod = useReactiveVar(paymentMethodVar)
  if (!loyaltyCard) {
    return null
  }

  const { discount } = loyaltyCard
  const loyaltyCardStamps = loyaltyCard.loyaltyCardStamps || []
  const amountToSpend =
    loyaltyCardStamps.length < loyaltyCard.requiredStamps
      ? loyaltyCard.amountToSpendToEarnAStamp ?? 0
      : 0

  const canStampBeEarned = loyaltyCard.isAvailableToday
    ? (amountToSpend === 0 &&
        (loyaltyCard.allowCashOrders ||
          paymentMethod.detailedPaymentMethod !==
            DetailedPaymentMethod.CASH)) ||
      !isBasket
    : false

  return (
    <OuterContainer>
      <LoyaltyCardLinkContainer
        showAnimation={
          loyaltyCard.loyaltyCardStamps.length === loyaltyCard.requiredStamps
        }
        isFinalCard={isFinalCard}
        singleBasketCard={singleBasketCard}
        loyaltyCardColor={loyaltyCard.loyaltyCardColor}
        route={{
          mainRouteName: MainRouteName.LOYALTY,
          pageDataId: discount?.id,
        }}
      >
        <LoyaltyCardInnerContainer>
          <LoyaltyCardIconAndTextContainer>
            <ScallopedMaskSVGLarge scale={0.6} />
            <LoyaltyCardImage
              isTermsPage={false}
              role="img"
              aria-label={discount?.name}
              canStampBeEarned={canStampBeEarned}
              imageUrl={imageJitURL(loyaltyCard.loyaltyCardIcon, {
                resize: {
                  width: 168,
                  height: 168,
                  fit: 'cover',
                },
              })}
              scale={0.6}
            />
            <DescriptionAndStampsContainer>
              <TitleAndDescriptionContainer>
                <LoyaltyCardTitle
                  loyaltyCardColor={loyaltyCard.loyaltyCardColor}
                >
                  {discount?.name}
                </LoyaltyCardTitle>
                <LoyaltyCardInfoText
                  loyaltyCard={loyaltyCard}
                  basketTotals={basketTotals}
                  isTermsPage={isTermsPage}
                  amountToSpendToEarnStamp={formatCurrency(amountToSpend)}
                  paymentMethod={paymentMethod.detailedPaymentMethod}
                  isBasket={isBasket}
                  applyAccountsSectionTextOverride={
                    useAccountsSectionTextOverride
                  }
                />
              </TitleAndDescriptionContainer>
              <LoyaltyCardStampProgressMeter
                isOutletList={false}
                loyaltyCard={loyaltyCard}
                canStampBeEarned={canStampBeEarned}
                basketTotals={basketTotals}
                isBasket={isBasket}
              />
            </DescriptionAndStampsContainer>
          </LoyaltyCardIconAndTextContainer>
        </LoyaltyCardInnerContainer>
      </LoyaltyCardLinkContainer>
    </OuterContainer>
  )
}
